import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import CrudSection from "./CrudSection";

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const onThisDayFields = [
  { name: "month", label: "Month (1-12)" },
  { name: "day", label: "Day (1-31)" },
  { name: "year", label: "Year" },
  { name: "title", label: "Title" },
  { name: "category", label: "Category", type: "select", options: ["Match", "Milestone", "Signing", "Fan Community", "Player"] },
  { name: "description", label: "Description", type: "textarea" },
];

/**
 * On This Day admin — wraps the generic CRUD grid and adds a "Backfill
 * Empty Days" action so Soft-404-triggering empty dates get filled with
 * LLM-generated placeholder entries.
 */
export default function OnThisDayAdmin() {
  const [emptyCount, setEmptyCount] = useState(null);
  const [sampleDates, setSampleDates] = useState([]);
  const [backfilling, setBackfilling] = useState(false);
  const [limit, setLimit] = useState(25);
  const [refreshingCount, setRefreshingCount] = useState(false);

  const loadEmptyCount = async () => {
    setRefreshingCount(true);
    try {
      const res = await fetch(`${API}/api/admin/on-this-day/empty-count`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setEmptyCount(data.empty_count);
      setSampleDates(data.sample || []);
    } catch (e) {
      toast.error(`Failed to load empty-day count: ${e.message}`);
    } finally {
      setRefreshingCount(false);
    }
  };

  useEffect(() => {
    loadEmptyCount();
  }, []);

  const triggerBackfill = async () => {
    setBackfilling(true);
    try {
      const res = await fetch(`${API}/api/admin/on-this-day/backfill?limit=${limit}`, {
        method: "POST",
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      toast.success(
        `OTD backfill running in the background (up to ${data.limit} dates). Come back in a few minutes and click Refresh.`
      );
      // Refresh count after a short delay so admins see progress
      setTimeout(loadEmptyCount, 10000);
    } catch (e) {
      toast.error(`Backfill failed to start: ${e.message}`);
    } finally {
      setBackfilling(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="on-this-day-admin">
      {/* Backfill panel */}
      <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" /> Backfill Empty Dates
            </h3>
            <p className="text-sm text-slate-500">
              Fills calendar dates that currently show "NO EVENTS RECORDED" with LLM-generated placeholder entries so Google stops flagging them as Soft 404s. Runs in the background — safe to close this panel while it works.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right mr-2">
              <div className="text-xs uppercase text-purple-500 font-semibold">Empty dates</div>
              <div className="text-2xl font-bold text-slate-800" data-testid="otd-empty-count">
                {emptyCount === null ? "…" : emptyCount}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={loadEmptyCount}
              disabled={refreshingCount}
              data-testid="otd-refresh-count"
            >
              <RefreshCw className={`w-4 h-4 ${refreshingCount ? "animate-spin" : ""}`} />
            </Button>
            <Input
              type="number"
              min={1}
              max={200}
              value={limit}
              onChange={(e) => setLimit(Math.max(1, Math.min(200, parseInt(e.target.value || "25", 10))))}
              className="w-24"
              data-testid="otd-backfill-limit"
              placeholder="Limit"
            />
            <Button
              onClick={triggerBackfill}
              disabled={backfilling || emptyCount === 0}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="otd-backfill-btn"
            >
              {backfilling ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting…</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" /> Backfill {limit} dates</>
              )}
            </Button>
          </div>
        </div>
        {sampleDates.length > 0 && (
          <div className="mt-3 pt-3 border-t border-purple-200 text-xs text-slate-600">
            <span className="font-semibold">Next in queue:</span>{" "}
            {sampleDates.map(([m, d]) => `${m}/${d}`).join(", ")}
            {emptyCount > sampleDates.length && <span> …and {emptyCount - sampleDates.length} more</span>}
          </div>
        )}
      </div>

      {/* Standard CRUD list */}
      <CrudSection
        title="ON THIS DAY"
        endpoint="on-this-day/all"
        writeEndpoint="on-this-day"
        fields={onThisDayFields}
        filterField={onThisDayFields[0]}
      />
    </div>
  );
}
